/**
 * Waveform peaks codec (T09).
 *
 * A track's waveform is exactly {@link PEAKS_RESOLUTION} `uint8` values — one per
 * bucket, `round(255 * max|sample| / 32768)` — produced at index time by the indexer
 * (`spec.md` §11.10) and shipped to the client base64-encoded in `tracks.peaks_b64`.
 *
 * Two hard rules from `spec.md` §11.10 / `design-direction.md` §6.7 shape this file:
 *
 * 1. **Peaks are never synthesised.** There is no hash, no PRNG and no fallback
 *    generator here. `null` peaks are a designed UI state (bar ruler + progress bar),
 *    not a hole to paper over.
 * 2. **The renderer emits ONE `<path>`**, never 512 `<rect>`s — see
 *    {@link peaksToSvgPath}.
 *
 * Portability: this module runs in the API (Node), the indexer (Node) *and* the web
 * bundle (browser). It therefore uses **no** host globals — no `Buffer`, no `atob`,
 * no `TextEncoder`. The base64 codec below is hand-rolled for that reason, and
 * because it lets `decodePeaks` reject hostile input precisely instead of silently
 * salvaging it the way `Buffer.from(s, 'base64')` does.
 */
/** Number of `uint8` peak buckets per track. Fixed by the wire format. */
export const PEAKS_RESOLUTION = 512;
const B64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
/** `charCode -> 6-bit value`, `-1` for every byte that is not in the alphabet. */
const B64_REVERSE = (() => {
    const table = new Array(128).fill(-1);
    for (let i = 0; i < B64_ALPHABET.length; i += 1) {
        table[B64_ALPHABET.charCodeAt(i)] = i;
    }
    return table;
})();
const B64_RE = /^[A-Za-z0-9+/]*={0,2}$/;
function b64Encode(bytes) {
    let out = '';
    const n = bytes.length;
    const whole = n - (n % 3);
    for (let i = 0; i < whole; i += 3) {
        const a = bytes[i] ?? 0;
        const b = bytes[i + 1] ?? 0;
        const c = bytes[i + 2] ?? 0;
        const triple = (a << 16) | (b << 8) | c;
        out +=
            B64_ALPHABET.charAt((triple >>> 18) & 63) +
                B64_ALPHABET.charAt((triple >>> 12) & 63) +
                B64_ALPHABET.charAt((triple >>> 6) & 63) +
                B64_ALPHABET.charAt(triple & 63);
    }
    const rest = n - whole;
    if (rest === 1) {
        const a = bytes[whole] ?? 0;
        out += B64_ALPHABET.charAt(a >>> 2) + B64_ALPHABET.charAt((a << 4) & 63) + '==';
    }
    else if (rest === 2) {
        const a = bytes[whole] ?? 0;
        const b = bytes[whole + 1] ?? 0;
        out +=
            B64_ALPHABET.charAt(a >>> 2) +
                B64_ALPHABET.charAt(((a << 4) | (b >>> 4)) & 63) +
                B64_ALPHABET.charAt((b << 2) & 63) +
                '=';
    }
    return out;
}
/**
 * Strict, canonical base64 decode. Throws `TypeError` on anything a canonical
 * encoder could not have produced: stray characters, a length that is not a
 * multiple of four, misplaced padding, or non-zero trailing bits (the
 * "two spellings of the same bytes" hole that makes a naive one-character
 * mutation test pass by accident).
 */
function b64Decode(text, what) {
    if (!B64_RE.test(text)) {
        throw new TypeError(`${what}: not base64 (illegal characters)`);
    }
    if (text.length % 4 !== 0) {
        throw new TypeError(`${what}: not base64 (length ${text.length} is not a multiple of 4)`);
    }
    if (text.length === 0)
        return new Uint8Array(0);
    let pad = 0;
    if (text.endsWith('=='))
        pad = 2;
    else if (text.endsWith('='))
        pad = 1;
    const groups = text.length / 4;
    const out = new Uint8Array(groups * 3 - pad);
    let o = 0;
    for (let g = 0; g < groups; g += 1) {
        const base = g * 4;
        const isLast = g === groups - 1;
        const quad = [0, 0, 0, 0];
        for (let j = 0; j < 4; j += 1) {
            const ch = text.charCodeAt(base + j);
            if (isLast && pad > 0 && j >= 4 - pad) {
                // Already known to be '=' by the regex + endsWith checks above.
                quad[j] = 0;
                continue;
            }
            const v = ch < 128 ? (B64_REVERSE[ch] ?? -1) : -1;
            if (v < 0)
                throw new TypeError(`${what}: not base64 (illegal character at ${base + j})`);
            quad[j] = v;
        }
        const triple = ((quad[0] ?? 0) << 18) | ((quad[1] ?? 0) << 12) | ((quad[2] ?? 0) << 6) | (quad[3] ?? 0);
        const bytes = isLast ? 3 - pad : 3;
        if (bytes > 0)
            out[o++] = (triple >>> 16) & 255;
        if (bytes > 1)
            out[o++] = (triple >>> 8) & 255;
        if (bytes > 2)
            out[o++] = triple & 255;
        if (isLast && pad > 0 && (triple & (pad === 2 ? 0xffff : 0xff)) !== 0) {
            throw new TypeError(`${what}: not canonical base64 (non-zero padding bits)`);
        }
    }
    return out;
}
/**
 * Encode exactly {@link PEAKS_RESOLUTION} peak bytes as base64.
 *
 * 512 bytes → 684 characters, padding included, for every input.
 *
 * @throws TypeError  when `peaks` is not a `Uint8Array`.
 * @throws RangeError when `peaks.length !== PEAKS_RESOLUTION`.
 */
export function encodePeaks(peaks) {
    if (!(peaks instanceof Uint8Array)) {
        throw new TypeError('encodePeaks: expected a Uint8Array');
    }
    if (peaks.length !== PEAKS_RESOLUTION) {
        throw new RangeError(`encodePeaks: expected ${PEAKS_RESOLUTION} peaks, got ${peaks.length}`);
    }
    return b64Encode(peaks);
}
/**
 * Decode a base64 peaks blob back to exactly {@link PEAKS_RESOLUTION} bytes.
 *
 * Rejects — rather than salvages — non-string input, non-base64 text,
 * non-canonical base64, and any payload whose decoded length is not 512.
 *
 * @throws TypeError  when the input is not a string or not canonical base64.
 * @throws RangeError when the decoded payload is not `PEAKS_RESOLUTION` bytes.
 */
export function decodePeaks(text) {
    if (typeof text !== 'string') {
        throw new TypeError('decodePeaks: expected a string');
    }
    // 684 chars is the only legal length; check it before doing any work so a
    // multi-megabyte string costs nothing.
    const expectedChars = Math.ceil(PEAKS_RESOLUTION / 3) * 4;
    if (text.length !== expectedChars) {
        throw new RangeError(`decodePeaks: expected ${expectedChars} base64 characters, got ${text.length}`);
    }
    const bytes = b64Decode(text, 'decodePeaks');
    if (bytes.length !== PEAKS_RESOLUTION) {
        throw new RangeError(`decodePeaks: expected ${PEAKS_RESOLUTION} peaks, got ${bytes.length}`);
    }
    return bytes;
}
function assertPositiveFinite(name, value) {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
        throw new RangeError(`peaksToSvgPath: ${name} must be a finite positive number, got ${String(value)}`);
    }
}
/** Two decimals is well under a device pixel at these sizes and keeps `d` short. */
function fmt(value) {
    const rounded = Math.round(value * 100) / 100;
    return rounded === 0 ? '0' : String(rounded);
}
/**
 * Render peaks as the `d` attribute of **one** `<path>`: a closed outline that walks
 * the top envelope left→right and the mirrored bottom envelope right→left around a
 * centre axis at `height / 2`.
 *
 * Why one path and not 512 rects: 512 elements is 512 layout/paint boxes on a phone
 * inside Telegram's webview, it cannot be filled with a single vertical gradient
 * (`design-direction.md` §6.7 wants flare→amber across the *band*), and the played /
 * unplayed split is done by clipping one path, not by re-styling hundreds of nodes.
 *
 * The output uses only `M`, `H`, `V` and a closing `Z` — no curves, matching the
 * guillotine-cut geometry rule (`design-direction.md` §5) — and never contains `NaN`:
 * every input that could produce one is rejected up front. Runs of buckets that round
 * to the same height are merged, so a quiet passage costs no commands.
 */
export function peaksToSvgPath(peaks, width, height) {
    if (!(peaks instanceof Uint8Array)) {
        throw new TypeError('peaksToSvgPath: expected a Uint8Array');
    }
    const n = peaks.length;
    if (n === 0) {
        throw new RangeError('peaksToSvgPath: peaks must not be empty');
    }
    assertPositiveFinite('width', width);
    assertPositiveFinite('height', height);
    const cy = height / 2;
    const tops = new Array(n);
    const bottoms = new Array(n);
    for (let i = 0; i < n; i += 1) {
        const amplitude = ((peaks[i] ?? 0) / 255) * cy;
        tops[i] = fmt(cy - amplitude);
        bottoms[i] = fmt(cy + amplitude);
    }
    const edge = (i) => fmt((i * width) / n);
    const parts = [];
    let cur = tops[0] ?? fmt(cy);
    parts.push(`M0 ${cur}`);
    for (let i = 1; i < n; i += 1) {
        const y = tops[i] ?? cur;
        if (y === cur)
            continue;
        parts.push(`H${edge(i)}`, `V${y}`);
        cur = y;
    }
    parts.push(`H${fmt(width)}`);
    cur = bottoms[n - 1] ?? fmt(cy);
    parts.push(`V${cur}`);
    for (let i = n - 2; i >= 0; i -= 1) {
        const y = bottoms[i] ?? cur;
        if (y === cur)
            continue;
        parts.push(`H${edge(i + 1)}`, `V${y}`);
        cur = y;
    }
    parts.push('H0', 'Z');
    return parts.join('');
}
//# sourceMappingURL=peaks.js.map